import { gql } from '@apollo/client';

export const EDITOR_TEMPLATES_ATTRIBUTES = gql`
  fragment EditorTemplateAttributes on Editor_templates {
    id
    objectId
    metadata
    editor_templates_pages {
      edges {
        node {
          canvasJson
          editor_project_formats_pointer {
            objectId
            name
          }
          editor_project_formats_default_pointer {
            objectId
            name
          }
          editor_templates_pointer {
            objectId
            name
          }
          editor_templates_default_pointer {
            objectId
            name
          }
          org_pointer {
            objectId
          }
          preview {
            name
            url
          }
          animations
          gifPreviewId
          hash
          order
        }
      }
    }
    name
    editor_project_types_pointer {
      objectId
      name
    }
    editor_project_types_default_pointer {
      objectId
      name
    }
    editor_templates_categories_pointer {
      objectId
      name
      order
    }
    editor_templates_categories_default_pointer {
      objectId
      name
      order
    }
    editor_project_formats_pointer {
      objectId
      name
    }
    editor_project_formats_default_pointer {
      objectId
      name
    }
  }
`;

export const EDITOR_TEMPLATES_DEFAULT_ATTRIBUTES = gql`
  fragment EditorTemplatesDefaultAttributes on Editor_templates_default {
    id
    objectId
    metadata
    editor_templates_default_pages {
      edges {
        node {
          canvasJson
          editor_project_formats_pointer {
            objectId
            name
          }
          editor_project_formats_default_pointer {
            objectId
            name
          }
          editor_templates_default_pointer {
            objectId
            name
          }
          org_pointer {
            objectId
          }
          preview {
            name
            url
          }
          animations
          gifPreviewId
          hash
          order
        }
      }
    }
    isGlobal
    name
    editor_project_types_default_pointer {
      objectId
      name
    }
    editor_templates_categories_default_pointer {
      objectId
      name
      order
    }
    editor_project_formats_default_pointer {
      objectId
    }
  }
`;

/**
 * Questa query prende sia i modelli default che custom.
 * I custom li prende una combinazione di filtri:
 *  - template custom, tipo default, formato default
 *  - template custom, tipo default, formato custom
 *  - template custom, tipo custom, formato default
 *  - template custom, tipo custom, formato custom
 *
 * Nota: quando si parla di format custom, ci si riferisce a due cose:
 * - i formati default che sono stati abbinati (tipi A2 Orizzontale)
 * - i formati creati da zero (tipo 50x50 cm)
 */
export const GET_TEMPLATES_DEFAULT = gql`
  ${EDITOR_TEMPLATES_ATTRIBUTES}
  ${EDITOR_TEMPLATES_DEFAULT_ATTRIBUTES}
  query GetEditorTemplatesDefault(
    $defaultProjectTypeId: ID
    $defaultFormatId: ID
    $formatId: ID
    $projectTypeId: ID
    $orgId: ID
    $limit: Int!
  ) {
    templateCustomTypeDefaultFormatDefault: findEditorTemplates(
      first: $limit
      where: {
        editor_project_formats_default_pointer: {
          have: { objectId: { equalTo: $defaultFormatId } }
        }
        editor_project_types_default_pointer: {
          have: { objectId: { equalTo: $defaultProjectTypeId } }
        }
        org_pointer: { have: { objectId: { equalTo: $orgId } } }
      }
    ) {
      count
      edges {
        node {
          ...EditorTemplateAttributes
        }
      }
    }
    templateCustomTypeDefaultFormatCustom: findEditorTemplates(
      first: $limit
      where: {
        editor_project_formats_default_pointer: { exists: false }
        editor_project_formats_pointer: {
          have: { objectId: { equalTo: $formatId } }
        }
        editor_project_types_default_pointer: {
          have: { objectId: { equalTo: $defaultProjectTypeId } }
        }
        org_pointer: { have: { objectId: { equalTo: $orgId } } }
      }
    ) {
      count
      edges {
        node {
          ...EditorTemplateAttributes
        }
      }
    }
    templateCustomTypeCustomFormatDefault: findEditorTemplates(
      first: $limit
      where: {
        editor_project_formats_default_pointer: {
          have: { objectId: { equalTo: $defaultFormatId } }
        }
        editor_project_types_pointer: {
          have: { objectId: { equalTo: $projectTypeId } }
        }
        org_pointer: { have: { objectId: { equalTo: $orgId } } }
      }
    ) {
      count
      edges {
        node {
          ...EditorTemplateAttributes
        }
      }
    }
    templateCustomTypeCustomFormatCustom: findEditorTemplates(
      first: $limit
      where: {
        editor_project_formats_pointer: {
          have: { objectId: { equalTo: $formatId } }
        }
        editor_project_types_pointer: {
          have: { objectId: { equalTo: $projectTypeId } }
        }
        org_pointer: { have: { objectId: { equalTo: $orgId } } }
      }
    ) {
      count
      edges {
        node {
          ...EditorTemplateAttributes
        }
      }
    }
    findEditorTemplatesDefault(
      first: $limit
      where: {
        isGlobal: { equalTo: true }
        editor_project_formats_default_pointer: {
          have: { objectId: { equalTo: $defaultFormatId } }
        }
        editor_project_types_default_pointer: {
          have: { objectId: { equalTo: $defaultProjectTypeId } }
        }
      }
    ) {
      count
      edges {
        node {
          ...EditorTemplatesDefaultAttributes
        }
      }
    }
    # Categories
    editorTemplatesDefaultCategories: findEditorTemplatesCategoriesDefault(
      order: order_ASC
      where: {
        editor_project_types_default_pointer: {
          have: { objectId: { equalTo: $defaultProjectTypeId } }
        }
      }
    ) {
      count
      edges {
        node {
          objectId
          name
          isGlobal
        }
      }
    }
    editorTemplatesCategoriesOfDefaultProjectType: findEditorTemplatesCategories(
      order: order_ASC
      where: {
        editor_project_types_default_pointer: {
          have: { objectId: { equalTo: $defaultProjectTypeId } }
        }
      }
    ) {
      count
      edges {
        node {
          objectId
          name
        }
      }
    }
    editorTemplatesCategoriesOfCustomProjectType: findEditorTemplatesCategories(
      order: order_ASC
      where: {
        editor_project_types_pointer: {
          have: { objectId: { equalTo: $projectTypeId } }
        }
      }
    ) {
      count
      edges {
        node {
          objectId
          name
        }
      }
    }
  }
`;

export const GET_TEMPLATE_DEFAULT_BY_ID = gql`
  query GetTemplateDefaultById($id: ID!) {
    getEditorTemplatesDefault(id: $id) {
      objectId
      editor_templates_default_pages {
        edges {
          node {
            objectId
            order
            canvasJson
            animations
            preview {
              name
              url
            }
          }
        }
      }
      isGlobal
      editor_templates_categories_default_pointer {
        objectId
        isGlobal
        name
        annunciFunebri
      }
    }
  }
`;

export const GET_TEMPLATE_BY_ID = gql`
  query GetTemplateById($id: ID!) {
    getEditorTemplates(id: $id) {
      objectId
      editor_templates_pages {
        edges {
          node {
            objectId
            order
            canvasJson
            animations
            preview {
              name
              url
            }
          }
        }
      }
      isGlobal
      editor_templates_categories_default_pointer {
        objectId
        isGlobal
        annunciFunebri
        name
      }
      editor_templates_categories_pointer {
        objectId
        name
      }
    }
  }
`;
